import { Box, Flex, Icon, IconicButton, Text, TruncatedText } from "@nulogy/components";
import UnstyledLink from "components/common/UnstyledLink";
import CustomWorkOrderFieldIndicator from "components/pages/schedule/workOrderCards/CustomWorkOrderFieldIndicator";
import DraggableCardTruncatedText from "components/pages/schedule/workOrderCards/DraggableCardTruncatedText";
import { getTooltipProps } from "components/pages/schedule/workOrderCards/helpers/TruncatedTextTooltipHelper";
import WorkOrderCardMenu from "components/pages/schedule/workOrderCards/WorkOrderCardMenu";
import WorkOrderDomain from "domain/WorkOrder";
import useUnscheduleWorkOrder from "hooks/apollo/workOrder/useUnscheduleWorkOrder";
import useUpdateWorkOrder from "hooks/apollo/workOrder/useUpdateWorkOrder";
import useSlideOutState from "hooks/state/useSlideOutState";
import Authorization from "policies/Authorization";
import React from "react";
import styled from "styled-components";

const HeaderContainer = styled(Flex)`
  flex-grow: 1;
  overflow: hidden; // allow the item to shrink to fit inside the container
`;

const WorkOrderCode = styled(TruncatedText)`
  cursor: pointer;
`;

const DragIcon = styled.div(({ schedulable, theme }) => {
  const color = schedulable ? theme.colors.darkBlue : theme.colors.grey;

  return {
    color,
    marginLeft: "-4px",

    svg: {
      verticalAlign: "middle",
    },
  };
});
export default function WorkOrderCardHeader({
  workOrder,
  expandedFields,
  layoutSetting,
  onToggleExpandWorkOrder,
  expanded,
  showOptions = true,
  highlightCustomWorkOrderFieldsIndicator = false,
}) {
  const handleUnscheduleWorkOrder = useUnscheduleWorkOrder();
  const updateWorkOrder = useUpdateWorkOrder();
  const policy = Authorization.usePolicy();
  const slideOutState = useSlideOutState();

  const anyExpandableFields = expandedFields.some(({ settingKey }) => layoutSetting[settingKey]);

  const userCanUpdate = policy.canUpdate(Authorization.EntityTypes.WorkOrder);
  const customFieldValue = WorkOrderDomain.getFirstCustomFieldValue(workOrder);

  const handleOpenWorkOrderDetails = () => {
    slideOutState.openWorkOrderDetails(workOrder.id);
  };

  return (
    <Flex alignItems="center" flexDirection="row" gap="half" height="x3">
      {userCanUpdate && (
        <DragIcon schedulable={workOrder.schedulable}>
          <Icon icon="drag" />
        </DragIcon>
      )}
      <CustomWorkOrderFieldIndicator
        description={customFieldValue?.description}
        highlight={highlightCustomWorkOrderFieldsIndicator}
        indicator={customFieldValue?.indicator}
        userCanUpdate={userCanUpdate}
      />
      <HeaderContainer alignItems="center" flex="1" gap="2px" height="x3" justifyContent="flex-start">
        <Box maxWidth="75%" mr="2px">
          <UnstyledLink color="darkBlue" draggable="false" fontSize="small" onClick={handleOpenWorkOrderDetails}>
            <WorkOrderCode
              className={`spec-work-order-${workOrder.id} pendo-view-work-order-details-work-order-card-code`}
              fontSize="small"
              fullWidth
              lineHeight="smallTextCompressed"
              tooltipProps={getTooltipProps(workOrder.code)}
            >
              {workOrder.code}
            </WorkOrderCode>
          </UnstyledLink>
        </Box>
        <Text color="midGrey" fontWeight="bold" inline ml="-1px" mr="2px">
          •
        </Text>
        <DraggableCardTruncatedText fontSize="small" grabbable={workOrder.schedulable} lineHeight="smallTextCompressed">
          {workOrder.customerName}
        </DraggableCardTruncatedText>
      </HeaderContainer>
      {anyExpandableFields && (
        <Box mr="-2px">
          <IconicButton
            data-testid="expand-work-order-button"
            icon={expanded ? "collapse" : "expand"}
            onClick={() => {
              onToggleExpandWorkOrder(workOrder.id);
            }}
            type="button"
          />
        </Box>
      )}

      {showOptions && (
        <Box mr="-5px">
          <WorkOrderCardMenu
            customiseWorkOrderCards
            onOpenWorkOrderDetails={handleOpenWorkOrderDetails}
            onUnscheduleWorkOrder={handleUnscheduleWorkOrder}
            updateWorkOrder={updateWorkOrder}
            userCanUpdate={userCanUpdate}
            workOrder={workOrder}
          />
        </Box>
      )}
    </Flex>
  );
}
